import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface MemberDetail {
  id: number
  login_id: string
  unique_member_no: string
  name: string
  phone: string
  email: string
  affiliation_name: string | null
  affiliation_type: string | null
  grade: string | null
  role: number
  is_active: number
  last_attendance: string | null
  total_points: number
}

const AFFILIATION_TYPE_OPTIONS = [
  { value: '', label: '선택 안함' },
  { value: 'elementary', label: '초등학교' },
  { value: 'middle', label: '중학교' },
  { value: 'high', label: '고등학교' },
  { value: 'university', label: '대학교' },
  { value: 'out_of_school', label: '학교 밖' },
  { value: 'other', label: '기타' },
  { value: 'unknown', label: '미입력' },
]

export default function AdminMemberDetail() {
  const { member_id } = useParams<{ member_id: string }>()
  const navigate = useNavigate()

  const [member, setMember] = useState<MemberDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // form state
  const [loginId, setLoginId] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [affiliationName, setAffiliationName] = useState('')
  const [affiliationType, setAffiliationType] = useState('')
  const [grade, setGrade] = useState('')
  const [role, setRole] = useState(0)
  const [isActive, setIsActive] = useState(1)
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // 하나네트웍스 관리자 여부 (role === 1 로 판단, 실제 조건에 맞게 수정)
  const currentUserRole = Number(localStorage.getItem('role') ?? '0')
  const isHanaAdmin = currentUserRole === 1

  useEffect(() => {
    fetchMember()
  }, [member_id])

  const fetchMember = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/member/admin/${member_id}`))
      if (!res.ok) throw new Error('조회 실패')
      const data: MemberDetail = await res.json()
      setMember(data)
      setLoginId(data.login_id)
      setName(data.name)
      setPhone(data.phone)
      setEmail(data.email)
      setAffiliationName(data.affiliation_name ?? '')
      setAffiliationType(data.affiliation_type ?? '')
      setGrade(data.grade ?? '')
      setRole(data.role)
      setIsActive(data.is_active)
    } catch {
      alert('회원 정보를 불러오는 데 실패했습니다.')
      navigate('/admin/member')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!window.confirm('수정 내용을 저장하시겠습니까?')) return
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        login_id: loginId || undefined,
        name: name || undefined,
        phone: phone || undefined,
        email: email || undefined,
        affiliation_name: affiliationName || undefined,
        affiliation_type: affiliationType || undefined,
        grade: grade || undefined,
      }
      if (isHanaAdmin && newPassword) {
        body.new_password = newPassword
      }

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/member/admin/${member_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail ?? '수정 실패')
      }

      const result = await res.json()
      alert(result.message ?? '수정되었습니다.')

      if (result.logout_required) {
        // 비밀번호 변경 시 해당 회원은 재로그인 필요
      }
      setNewPassword('')
      fetchMember()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '수정에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
        불러오는 중...
      </div>
    )
  }

  if (!member) return null

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[760px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">회원 상세 / 수정</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/member')}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            ← 목록으로
          </button>
        </div>

        {/* Read-only summary card */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-4 mb-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-6 items-center">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] text-gray-400">회원 ID</span>
            <span className="text-[14px] font-bold text-[#2a1f1a]">#{member.id}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] text-gray-400">고유회원번호</span>
            <span className="text-[14px] font-bold text-[#2a1f1a]">{member.unique_member_no}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] text-gray-400">포인트</span>
            <span className="text-[14px] font-bold text-[#887267]">{member.total_points.toLocaleString()}p</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] text-gray-400">마지막 출석</span>
            <span className="text-[13px] text-[#2a1f1a]">{formatDate(member.last_attendance)}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] text-gray-400">계정 상태</span>
            <span className={`text-[11px] px-2 py-0.5 rounded font-medium w-fit ${
              member.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
            }`}>
              {member.is_active ? '활성' : '비활성'}
            </span>
          </div>
        </div>

        {/* Edit form */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">

          <SectionTitle>기본 정보</SectionTitle>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-6">
            <Field label="아이디">
              <input
                value={loginId}
                onChange={e => setLoginId(e.target.value)}
                className={inputCls}
                placeholder="아이디"
              />
            </Field>
            <Field label="이름">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                className={inputCls}
                placeholder="이름"
              />
            </Field>
            <Field label="전화번호">
              <input
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className={inputCls}
                placeholder="전화번호"
              />
            </Field>
            <Field label="이메일">
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={inputCls}
                placeholder="이메일"
                type="email"
              />
            </Field>
            <Field label="회원 권한">
              <select
                value={role}
                onChange={e => setRole(Number(e.target.value))}
                className={inputCls}
              >
                <option value={0}>일반</option>
                <option value={1}>관리자</option>
              </select>
            </Field>
            <Field label="활성 상태">
              <select
                value={isActive}
                onChange={e => setIsActive(Number(e.target.value))}
                className={inputCls}
              >
                <option value={1}>활성</option>
                <option value={0}>비활성</option>
              </select>
            </Field>
          </div>

          <div className="border-t border-[#f5f2f0] my-4" />
          <SectionTitle>소속 정보</SectionTitle>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-6">
            <Field label="소속 (학교명)">
              <input
                value={affiliationName}
                onChange={e => setAffiliationName(e.target.value)}
                className={inputCls}
                placeholder="소속 이름"
              />
            </Field>
            <Field label="소속 유형">
              <select
                value={affiliationType}
                onChange={e => setAffiliationType(e.target.value)}
                className={inputCls}
              >
                {AFFILIATION_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>
            <Field label="학년">
              <input
                value={grade}
                onChange={e => setGrade(e.target.value)}
                className={inputCls}
                placeholder="예: 3학년"
              />
            </Field>
          </div>

          {/* Password section — 하나네트웍스 관리자만 */}
          {isHanaAdmin && (
            <>
              <div className="border-t border-[#f5f2f0] my-4" />
              <SectionTitle>비밀번호 변경 <span className="text-[11px] font-normal text-[#c0a898] ml-1">(관리자 전용)</span></SectionTitle>
              <div className="mt-4 mb-2 max-w-[340px]">
                <Field label="새 비밀번호">
                  <div className="relative">
                    <input
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      type={showPassword ? 'text' : 'password'}
                      className={inputCls + ' pr-10'}
                      placeholder="8~16자, 영문/숫자/특수문자 포함"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer text-[12px]"
                    >
                      {showPassword ? '숨김' : '표시'}
                    </button>
                  </div>
                </Field>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="border-t border-[#f5f2f0] mt-6 pt-5 flex justify-end gap-2">
            <button
              onClick={() => navigate('/admin/member')}
              className="text-[13px] text-[#887267] font-semibold px-5 py-2.5 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#887267] text-white text-[13px] font-semibold px-6 py-2.5 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? '저장 중...' : '수정하기'}
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

// ─── helpers ───────────────────────────────────────────────────────────────

const inputCls =
  'w-full border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors bg-white'

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[13px] font-bold text-[#5a3d32]">{children}</h2>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] text-[#5a3d32] font-medium">{label}</label>
      {children}
    </div>
  )
}